import { Skeleton } from "~/components/ui/skeleton";
import { m } from "~/lib/i18n";
import { cn } from "~/lib/utils";

/** Keep loading marks visible above dark panels without adding outlines. */
export function StocksSkeleton({ className }: { className?: string }) {
  return (
    <Skeleton
      animation="none"
      className={cn("dark:[--muted:var(--surface-3)]", className)}
    />
  );
}

// Presentation only: this module is also used by the eager route shell.
export const STOCKS_TICKER_ITEM_CLASSNAME =
  "relative w-[13.5rem] shrink-0 snap-start after:absolute after:inset-y-3 after:right-0 after:w-px after:bg-border/60 last:after:hidden";
// Share fixed slots so prices, logos, and loading placeholders cannot resize the strip.
export const STOCKS_TICKER_ITEM_CONTENT_CLASSNAME =
  "mx-1 grid h-12 grid-cols-[2rem_minmax(0,1fr)_4rem] items-center gap-3 px-2";

export const STOCKS_TICKER_CONTAINER_CLASSNAME =
  "mx-auto flex h-16 w-full max-w-8xl items-center gap-3 px-4";
export const STOCKS_TICKER_LIST_CLASSNAME =
  "scroll-fade-x flex h-16 min-w-0 flex-1 py-2";

export function StocksTickerLoadingItems() {
  return (
    <>
      {Array.from({ length: 10 }, (_, index) => (
        <li
          aria-hidden="true"
          className={STOCKS_TICKER_ITEM_CLASSNAME}
          key={index}
        >
          <div className={STOCKS_TICKER_ITEM_CONTENT_CLASSNAME}>
            <StocksSkeleton className="size-8 rounded-full" />
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="flex h-5 items-center">
                <StocksSkeleton className="h-3.5 w-10" />
              </div>
              <div className="flex h-5 items-center">
                <StocksSkeleton className="h-3 w-14" />
              </div>
            </div>
            <StocksSkeleton className="h-3 w-12 justify-self-end" />
          </div>
        </li>
      ))}
    </>
  );
}

export function StocksTickerStripPending() {
  return (
    <div aria-hidden="true" className="relative z-10 bg-surface-1">
      <div className={STOCKS_TICKER_CONTAINER_CLASSNAME}>
        <ul className={cn(STOCKS_TICKER_LIST_CLASSNAME, "overflow-hidden")}>
          <StocksTickerLoadingItems />
        </ul>
        <div className="hidden w-15 shrink-0 sm:block" />
      </div>
    </div>
  );
}

// The separator belongs to the 72px slot, outside the rounded interactive row.
export const STOCK_MOVER_SLOT_CLASS =
  "@container/mover-row relative py-1 after:pointer-events-none after:absolute after:inset-x-3 after:bottom-0 after:h-px after:bg-border/50 last:after:hidden";
export const STOCK_MOVER_ROW_CLASS =
  "grid h-16 w-full grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-3 @[27rem]/mover-row:grid-cols-[2.25rem_minmax(0,1fr)_5rem_auto]";
export const STOCK_MOVER_CHART_CLASS =
  "hidden h-7 w-20 shrink-0 @[27rem]/mover-row:block";
export const STOCK_MOVER_QUOTE_CLASS =
  "flex h-6 w-36 items-baseline justify-end gap-2 whitespace-nowrap tabular-nums";

export function StockMoverLoadingRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div
          className={STOCK_MOVER_SLOT_CLASS}
          data-testid="stock-mover-slot"
          key={index}
        >
          <div aria-hidden="true" className={STOCK_MOVER_ROW_CLASS}>
            <StocksSkeleton className="size-9 rounded-full" />
            <div className="min-w-0">
              <div className="flex h-6 items-center">
                <StocksSkeleton className="h-4 w-12" />
              </div>
              <div className="mt-0.5 flex h-5 items-center">
                <StocksSkeleton className="h-3 w-4/5" />
              </div>
            </div>
            <div className={STOCK_MOVER_CHART_CLASS}>
              <div className="flex h-full items-center">
                <StocksSkeleton className="h-3 w-full" />
              </div>
            </div>
            <div className={cn(STOCK_MOVER_QUOTE_CLASS, "items-center")}>
              <StocksSkeleton className="h-3.5 w-12" />
              <StocksSkeleton className="h-4 w-14" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

export interface MarketFundConfig {
  readonly id: "iwm" | "qqq" | "spy" | "tlt";
  readonly ticker: string;
}

export const MARKET_FUNDS = [
  { id: "spy", ticker: "SPY" },
  { id: "qqq", ticker: "QQQ" },
  { id: "iwm", ticker: "IWM" },
  { id: "tlt", ticker: "TLT" },
] as const satisfies readonly MarketFundConfig[];

export function getMarketFundLabel(id: MarketFundConfig["id"]): string {
  switch (id) {
    case "spy":
      return m.stocks_market_indicators_sp500_etf_label();
    case "qqq":
      return m.stocks_market_indicators_nasdaq_100_etf_label();
    case "iwm":
      return m.stocks_market_indicators_russell_2000_etf_label();
    case "tlt":
      return m.stocks_market_indicators_treasury_etf_label();
  }
}

export const MARKET_FUND_CARD_CLASSNAME =
  "relative isolate flex h-30 min-w-0 flex-col overflow-hidden page-surface-panel rounded-lg p-4 text-left";

export function MarketIndicatorsPending() {
  return (
    <section className="space-y-3" data-testid="stocks-market-overview">
      <div className="flex h-6 items-center justify-between gap-3">
        <h2 className="shrink-0 text-title-sm font-medium text-foreground">
          {m.asset_page_market_snapshot_title()}
        </h2>
        <StocksSkeleton className="h-3 w-36" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {MARKET_FUNDS.map((fund) => (
          <div
            className={MARKET_FUND_CARD_CLASSNAME}
            data-testid={`market-fund-card-${fund.ticker}`}
            key={fund.id}
          >
            <div className="relative z-10 flex min-w-0 items-baseline justify-between gap-2">
              <p className="line-clamp-2 text-meta font-medium text-foreground">
                {getMarketFundLabel(fund.id)}
              </p>
              {/* i18n-exempt: fund ticker is a fixed market identifier. */}
              <p className="shrink-0 text-caption text-muted-foreground">
                {fund.ticker}
              </p>
            </div>
            <div
              aria-hidden="true"
              className="relative z-10 mt-2 flex h-7 min-w-0 items-baseline gap-2"
            >
              <div className="flex h-7 shrink-0 items-center">
                <StocksSkeleton className="h-5 w-24" />
              </div>
              <div className="flex h-5 shrink-0 items-center">
                <StocksSkeleton className="h-3 w-14" />
              </div>
            </div>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-16"
            >
              <StocksSkeleton className="h-full w-full rounded-none opacity-30 [mask-image:linear-gradient(to_top,black,transparent)]" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
